// src/components/foods/FoodStock.jsx
import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import "./foods.css"; // uses the same modal/table styles you already have

import { api } from "../../services/api";
import { fetchKhNames } from "../../services/foods";

/* ---------------- utils (same style as Foods.jsx) ---------------- */
function pad2(n) {
  const x = String(n);
  return x.length === 1 ? `0${x}` : x;
}
function todayDmy() {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}
function fmt2(x) {
  const n = Number(x || 0);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}
function numStr(v) {
  const s = String(v ?? "").trim();
  if (!s) return "0";
  const n = Number(s);
  return Number.isFinite(n) ? String(n) : "0";
}
function safeParseArray(v) {
  try {
    const s = typeof v === "string" ? v.trim() : v;
    const out = typeof s === "string" ? JSON.parse(s) : s;
    return Array.isArray(out) ? out : [];
  } catch {
    return [];
  }
}

// axios POST form helper
function toForm(data) {
  const form = new URLSearchParams();
  Object.entries(data || {}).forEach(([k, v]) => form.append(k, String(v ?? "")));
  return form;
}

/* ---------------- Portal Modal ---------------- */
function PortalModal({ children }) {
  return ReactDOM.createPortal(children, document.body);
}

/* ---------------- Party Picker Modal (Dealer picker) ----------------
   Android: getkhpartmd(mainuser)
   NOTE: filename must match your server.
   If your PHP is named differently, change only PARTY_ENDPOINT below.
*/
const PARTY_ENDPOINT = "getkhpartmd.php"; // <-- CHANGE IF YOUR SERVER FILE NAME IS DIFFERENT

async function fetchKhParties({ mainuser }) {
  // Android uses GET, but many of your PHP endpoints also accept POST.
  // We'll try GET first, fallback POST.
  try {
    const res = await api.get(PARTY_ENDPOINT, { params: { mainuser } });
    return res.data;
  } catch {
    const res = await api.post(
      PARTY_ENDPOINT,
      toForm({ mainuser }),
      { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
    );
    return res.data;
  }
}

function PartyPickerModal({ open, onClose, onPick, mainuser }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;

    (async () => {
      setLoading(true);
      try {
        const data = await fetchKhParties({ mainuser });
        const arr = safeParseArray(data);

        const mapped = arr
          .map((o, idx) => ({
            sr: o?.sr ?? String(idx + 1),
            kno: String(o?.kno ?? o?.code ?? "").trim(),
            name: String(o?.name ?? o?.cname ?? "").trim(),
            address: String(o?.address ?? "").trim(),
          }))
          .filter((r) => r.kno);

        setRows(mapped);
      } catch (e) {
        console.error("party fetch failed", e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, mainuser]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        String(r.kno).toLowerCase().includes(s) ||
        String(r.name).toLowerCase().includes(s) ||
        String(r.address).toLowerCase().includes(s)
    );
  }, [rows, q]);

  if (!open) return null;

  return (
    <PortalModal>
      <div className="fdBackdrop" onMouseDown={onClose}>
        <div className="fdModal fdPickModal" onMouseDown={(e) => e.stopPropagation()}>
          <div className="fdModalHead">
            <div className="fdPickHeadTitle">Dealers</div>
            <button type="button" className="fdCloseBtn" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          <div className="fdPickTop">
            <div className="fdPickCaption">Dealers</div>
            <div className="foodsSearchBox">
              <span className="foodsSearchLabel">Search</span>
              <input
                className="foodsInput"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search"
              />
            </div>
          </div>

          <div className="foodsTableWrap">
            <table className="foodsTable" style={{ minWidth: 820 }}>
              <thead>
                <tr>
                  <th>Dealer Code</th>
                  <th>Name</th>
                  <th>Address</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} className="foodsEmpty">Loading...</td></tr>
                ) : visible.length === 0 ? (
                  <tr><td colSpan={3} className="foodsEmpty">No dealers</td></tr>
                ) : (
                  visible.map((r) => (
                    <tr
                      key={`${r.kno}-${r.sr}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => onPick(r)}
                      title="Click to select"
                    >
                      <td>{r.kno}</td>
                      <td><b>{r.name || "-"}</b></td>
                      <td>{r.address || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="foodsFooter">
            <div style={{ color: "#666" }}>
              Showing 1 to {visible.length} of {visible.length} entries
            </div>
            <div />
          </div>
        </div>
      </div>
    </PortalModal>
  );
}

/* ---------------- Food Picker Modal (reused from stock list source) ---------------- */
function FoodPickerModal({ open, onClose, onPick, mainuser }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  const purdate = localStorage.getItem("purdate") || "";

  useEffect(() => {
    if (!open) return;

    (async () => {
      setLoading(true);
      try {
        const data = await fetchKhNames({ mainuser, purdate });
        const arr = safeParseArray(data);

        const mapped = arr
          .map((o, idx) => ({
            code: o?.code ?? String(idx + 1),
            khname: String(o?.khname ?? "").trim(),
            current_stock: String(o?.current_stock ?? "").trim(),
            rate: String(o?.rate ?? "").trim(),   // purchase rate in many installs
            srate: String(o?.srate ?? "").trim(), // sale rate
            date: String(o?.date ?? "").trim(),
          }))
          .filter((r) => r.khname);

        setRows(mapped);
      } catch (e) {
        console.error("food list fetch failed", e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, mainuser, purdate]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(
      (r) =>
        String(r.code).toLowerCase().includes(s) ||
        String(r.khname).toLowerCase().includes(s)
    );
  }, [rows, q]);

  if (!open) return null;

  return (
    <PortalModal>
      <div className="fdBackdrop" onMouseDown={onClose}>
        <div className="fdModal fdPickModal" onMouseDown={(e) => e.stopPropagation()}>
          <div className="fdModalHead">
            <div className="fdPickHeadTitle">Foods</div>
            <button type="button" className="fdCloseBtn" onClick={onClose} aria-label="Close">
              ×
            </button>
          </div>

          <div className="fdPickTop">
            <div className="fdPickCaption">Foods</div>
            <div className="foodsSearchBox">
              <span className="foodsSearchLabel">Search</span>
              <input
                className="foodsInput"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search"
              />
            </div>
          </div>

          <div className="foodsTableWrap">
            <table className="foodsTable" style={{ minWidth: 820 }}>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th className="foodsNum">Quantity</th>
                  <th className="foodsNum">Sale Rate</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} className="foodsEmpty">Loading...</td></tr>
                ) : visible.length === 0 ? (
                  <tr><td colSpan={4} className="foodsEmpty">No foods</td></tr>
                ) : (
                  visible.map((r) => (
                    <tr
                      key={`${r.code}-${r.khname}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => onPick(r)}
                      title="Click to select"
                    >
                      <td>{r.code}</td>
                      <td><b>{r.khname}</b></td>
                      <td className="foodsNum">{r.current_stock || "-"}</td>
                      <td className="foodsNum">{fmt2(r.srate || r.rate)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="foodsFooter">
            <div style={{ color: "#666" }}>
              Showing 1 to {visible.length} of {visible.length} entries
            </div>
            <div />
          </div>
        </div>
      </div>
    </PortalModal>
  );
}

/* ---------------- Add/Edit Food Stock Modal ----------------
   Android save: savekhpurapp2.php  (FormUrlEncoded)
*/
async function saveKhPurchase(payload) {
  const res = await api.post(
    "savekhpurapp2.php",
    toForm(payload),
    { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
  );
  return res.data;
}

function AddFoodStockModal({ open, onClose, onSaved, mainuser, editRow }) {
  const [dealerCode, setDealerCode] = useState("");
  const [dealerName, setDealerName] = useState("");

  const [dateDmy, setDateDmy] = useState(todayDmy());

  const [khname, setKhname] = useState("");
  const [quantity, setQuantity] = useState("");
  const [prate, setPrate] = useState("");
  const [pamt, setPamt] = useState("0");
  const [srate, setSrate] = useState("");
  const [paid, setPaid] = useState("");

  const [saveContinue, setSaveContinue] = useState(false);

  const [saving, setSaving] = useState(false);
  const [partyPickOpen, setPartyPickOpen] = useState(false);
  const [foodPickOpen, setFoodPickOpen] = useState(false);

  // init (edit/new)
  useEffect(() => {
    if (!open) return;

    if (editRow) {
      setDealerCode(String(editRow.kno ?? ""));
      setDealerName(String(editRow.dealerName ?? editRow.name ?? "")); // best-effort
      setDateDmy(String(editRow.dateDmy ?? editRow.date ?? todayDmy()));
      setKhname(String(editRow.khname ?? ""));
      setQuantity(String(editRow.quin ?? editRow.quantity ?? ""));
      setPrate(String(editRow.rate ?? editRow.prate ?? ""));
      setSrate(String(editRow.srate ?? ""));
      setPaid(String(editRow.paid ?? ""));
      setSaveContinue(false);
    } else {
      setDealerCode("");
      setDealerName("");
      setDateDmy(todayDmy());
      setKhname("");
      setQuantity("");
      setPrate("");
      setSrate("");
      setPaid("");
      setPamt("0");
      setSaveContinue(false);
    }
  }, [open, editRow]);

  // purchase amount calc
  useEffect(() => {
    const r = Number(prate || 0);
    const q = Number(quantity || 0);
    if (Number.isFinite(r) && Number.isFinite(q) && r > 0 && q > 0) {
      setPamt(String(r * q));
    } else {
      setPamt("0");
    }
  }, [prate, quantity]);

  function validate() {
    if (!String(dealerCode || "").trim()) return "Dealer code required";
    if (!String(dateDmy || "").trim()) return "Date required";
    if (!String(khname || "").trim()) return "Name required";
    if (!(Number(quantity) > 0)) return "Quantity required";
    if (!(Number(prate) > 0)) return "Purchase rate required";
    if (!(Number(srate) > 0)) return "Sale rate required";
    if (!(Number(paid) >= 0)) return "Amount paid required";
    return "";
  }

  function resetOnlyFields() {
    setKhname("");
    setQuantity("");
    setPrate("");
    setSrate("");
    setPaid("");
    setPamt("0");
  }

  async function onSave() {
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    setSaving(true);
    try {
      // matches Android savekhpurmd fields
      const payload = {
        mainuser: String(mainuser || ""),
        date: String(dateDmy || ""),
        kno: String(dealerCode || ""),
        khname: String(khname || ""),
        khcompany: "-",                 // Android passes "-"
        quin: numStr(quantity),
        rate: numStr(prate),
        srate: numStr(srate),
        amt: numStr(pamt),              // purchase amount
        sredit: editRow?.sr ? String(editRow.sr) : "-", // if you have sr
        flag: editRow ? "edit" : "new",

        // extra safe fields (ignored if backend doesn't use)
        paid: numStr(paid),
      };

      await saveKhPurchase(payload);

      await onSaved?.();

      if (saveContinue) {
        resetOnlyFields();
      } else {
        onClose?.();
      }
    } catch (e) {
      alert(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <>
      <PortalModal>
        <div className="fdBackdrop" onMouseDown={onClose}>
          <div className="fdModal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="fdModalHead">
              <div className="fdModalHeadTitle">{editRow ? "Edit Food Stock" : "Add Food Stock"}</div>
              <button type="button" className="fdCloseBtn" onClick={onClose} aria-label="Close">
                ×
              </button>
            </div>

            <div className="fdModalBody">
              <div className="fdFormGrid">
                <div className="fdLabel">
                  Dealer code : <span className="fdReq">*</span>
                </div>
                <div className="fdFieldRow">
                  <input
                    className="fdInput"
                    value={dealerCode}
                    onChange={(e) => setDealerCode(e.target.value)}
                    placeholder=""
                  />
                  <button
                    type="button"
                    className="fdIconBtn"
                    title="Select dealer"
                    onClick={() => setPartyPickOpen(true)}
                  >
                    🔍
                  </button>
                </div>

                <div />
                <div style={{ fontSize: 12, opacity: 0.85 }}>
                  {dealerName ? <b>{dealerName}</b> : "\u00A0"}
                </div>

                <div className="fdLabel">
                  Date : <span className="fdReq">*</span>
                </div>
                <div>
                  <input
                    className="fdInput"
                    value={dateDmy}
                    onChange={(e) => setDateDmy(e.target.value)}
                    placeholder="dd/MM/yyyy"
                  />
                </div>

                <div className="fdLabel">
                  Name : <span className="fdReq">*</span>
                </div>
                <div className="fdFieldRow">
                  <input className="fdInput" value={khname} readOnly placeholder="" />
                  <button
                    type="button"
                    className="fdIconBtn"
                    title="Select food"
                    onClick={() => setFoodPickOpen(true)}
                  >
                    🔍
                  </button>
                </div>

                <div className="fdLabel">
                  Quantity : <span className="fdReq">*</span>
                </div>
                <div>
                  <input className="fdInput" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
                </div>

                <div className="fdLabel">
                  Purchase Rate : <span className="fdReq">*</span>
                </div>
                <div>
                  <input className="fdInput" value={prate} onChange={(e) => setPrate(e.target.value)} />
                </div>

                <div className="fdLabel">Purchase Amount :</div>
                <div>
                  <input className="fdInput" value={fmt2(pamt)} readOnly style={{ background: "#eef2f5" }} />
                </div>

                <div className="fdLabel">
                  Sale Rate : <span className="fdReq">*</span>
                </div>
                <div>
                  <input className="fdInput" value={srate} onChange={(e) => setSrate(e.target.value)} />
                </div>

                <div className="fdLabel">
                  Amount Paid : <span className="fdReq">*</span>
                </div>
                <div>
                  <input className="fdInput" value={paid} onChange={(e) => setPaid(e.target.value)} />
                </div>

                <div />
                <div className="fdCheckboxRow">
                  <label>
                    <input
                      type="checkbox"
                      checked={saveContinue}
                      onChange={(e) => setSaveContinue(e.target.checked)}
                    />{" "}
                    Save and continue :
                  </label>
                </div>
              </div>

              <div className="fdActions">
                <button
                  type="button"
                  className="fdBtnGhost"
                  onClick={resetOnlyFields}
                  disabled={saving}
                >
                  Reset
                </button>
                <button type="button" className="fdBtnPrimary" onClick={onSave} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </PortalModal>

      <PartyPickerModal
        open={partyPickOpen}
        onClose={() => setPartyPickOpen(false)}
        mainuser={mainuser}
        onPick={(p) => {
          setDealerCode(String(p.kno || ""));
          setDealerName(String(p.name || ""));
          setPartyPickOpen(false);
        }}
      />

      <FoodPickerModal
        open={foodPickOpen}
        onClose={() => setFoodPickOpen(false)}
        mainuser={mainuser}
        onPick={(f) => {
          setKhname(f.khname);
          // best-effort: use rate/srate from server if present
          setPrate(String(f.rate || ""));
          setSrate(String(f.srate || f.rate || ""));
          setFoodPickOpen(false);
        }}
      />
    </>
  );
}

/* ---------------- Main FoodStock page ---------------- */
export default function FoodStock({ mainuser, onBack }) {
  const purdate = localStorage.getItem("purdate") || "";
  const companyLabel =
    localStorage.getItem("companyname") ||
    localStorage.getItem("mainname") ||
    localStorage.getItem("societyname") ||
    "";

  const [stockOnly, setStockOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");

  const [addOpen, setAddOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  async function loadStock() {
    if (!mainuser) return;
    setLoading(true);
    try {
      const data = await fetchKhNames({ mainuser, purdate });
      const arr = safeParseArray(data);

      const mapped = arr
        .map((o, idx) => ({
          srno: idx + 1,
          sr: o?.sr ?? "",
          code: o?.code ?? String(idx + 1),
          khname: String(o?.khname ?? "").trim(),
          current_stock: String(o?.current_stock ?? "").trim(),
          srate: String(o?.srate ?? "").trim(),
          rate: String(o?.rate ?? "").trim(),
          date: String(o?.date ?? "").trim(),
          kno: String(o?.kno ?? "").trim(),
        }))
        .filter((r) => r.khname);

      setRows(mapped);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadStock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainuser, purdate]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();

    let out = rows;
    if (stockOnly) {
      out = out.filter((r) => Number(r.current_stock || 0) > 0);
    }
    if (!q) return out;

    return out.filter(
      (r) =>
        String(r.code || "").toLowerCase().includes(q) ||
        String(r.khname || "").toLowerCase().includes(q)
    );
  }, [rows, search, stockOnly]);

  return (
    <div className="foodsPage">
      <div className="foodsTop">
        <div className="foodsTitle" style={{ gap: 10 }}>
          <button
            type="button"
            className="foodsLinkBtn"
            title="Back"
            onClick={() => (onBack ? onBack() : null)}
            style={{ fontSize: 18 }}
          >
            ←
          </button>
          <span>
            Food Stock {companyLabel ? `(${companyLabel})` : ""}
          </span>
        </div>

        <div className="foodsActions">
          <button
            className="foodsLinkBtn"
            onClick={() => {
              setEditRow(null);
              setAddOpen(true);
            }}
          >
            ＋ Add Food Stock
          </button>
        </div>
      </div>

      <div className="foodsCard">
        <div className="foodsCardHead">
          <div className="foodsCaption">
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={stockOnly}
                onChange={(e) => setStockOnly(e.target.checked)}
              />
              Stock Only
            </label>
          </div>

          <div className="foodsSearchBox">
            <span className="foodsSearchLabel">Search</span>
            <input
              className="foodsInput"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
            />
          </div>
        </div>

        <div className="foodsTableWrap">
          <table className="foodsTable">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th className="foodsNum">Quantity</th>
                <th className="foodsNum">Sale Rate</th>
                <th style={{ width: 90 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="foodsEmpty">Loading...</td></tr>
              ) : visible.length === 0 ? (
                <tr><td colSpan={5} className="foodsEmpty">No rows</td></tr>
              ) : (
                visible.map((r) => (
                  <tr key={`${r.code}-${r.khname}`}>
                    <td>{r.code}</td>
                    <td><b>{r.khname}</b></td>
                    <td className="foodsNum">{r.current_stock || "-"}</td>
                    <td className="foodsNum">{fmt2(r.srate || r.rate)}</td>
                    <td>
                      <button
                        className="foodsLinkBtn"
                        type="button"
                        title="Edit"
                        onClick={() => {
                          setEditRow(r);
                          setAddOpen(true);
                        }}
                      >
                        ✎
                      </button>
                      <button className="foodsLinkBtn" type="button" title="Delete" style={{ color: "red" }}>
                        🗑
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="foodsFooter">
          <div style={{ color: "#666" }}>
            Showing {visible.length ? 1 : 0} to {visible.length} of {visible.length} entries
          </div>
          <div />
        </div>
      </div>

      <AddFoodStockModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={loadStock}
        mainuser={mainuser}
        editRow={editRow}
      />
    </div>
  );
}
